/**
 * RefillReturnToast — render shell for `useRefillReturnToast`.
 *
 * ADR-0108 phase 3. Mounted once at the App shell (P7-050) so Stripe
 * return on /profile, /planet, /imagine, and /jub all share one observer.
 * Do not also mount this inside JubApp — two hooks would both strip
 * `?refill=` and the toast can double-fire.
 *
 * Visual matches the existing inline toast pattern from
 * DetailScreen.tsx line 478: pill-shaped status with
 * `role="status"` + `aria-live="polite"` so screen readers
 * announce the refill outcome without stealing focus.
 */

import type { CSSProperties } from 'react';
import { useRefillReturnToast } from './useRefillReturnToast.ts';

const TOAST_BASE: CSSProperties = {
  position: 'fixed',
  bottom: 24,
  left: '50%',
  transform: 'translateX(-50%)',
  background: 'rgba(15, 12, 18, 0.92)',
  color: '#fff',
  fontSize: 12,
  fontWeight: 600,
  padding: '8px 16px',
  borderRadius: 999,
  pointerEvents: 'none',
  zIndex: 1000,
  maxWidth: '90vw',
  textAlign: 'center',
};

const SUCCESS_ACCENT: CSSProperties = {
  border: '1px solid rgba(34, 197, 94, 0.45)',
  boxShadow: '0 4px 18px rgba(34, 197, 94, 0.18)',
};

const CANCEL_ACCENT: CSSProperties = {
  border: '1px solid rgba(244, 114, 182, 0.45)',
  boxShadow: '0 4px 18px rgba(244, 114, 182, 0.18)',
};

export function RefillReturnToast(): JSX.Element | null {
  const state = useRefillReturnToast();
  if (state.kind === 'idle') return null;

  const accent = state.kind === 'success' ? SUCCESS_ACCENT : CANCEL_ACCENT;

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid={`refill-return-toast-${state.kind}`}
      style={{ ...TOAST_BASE, ...accent }}
    >
      {state.message}
    </div>
  );
}
